import { useEffect } from 'react'
import { Route, useHistory } from 'react-router-dom'
import { clearStorage, getUser } from '_helpers/storage'
import { client } from '_services'
import { PrivateRouteProps } from './types'

export const PrivateRoute =
  <T extends Record<string, unknown> = Record<string, unknown>, Path extends string = string>(
    {
      idleTimeout = 900000,
      warningTimeout = 850000,
      component,
      render,
      ...routeProps
    }: PrivateRouteProps<T, Path>) => {
    const history = useHistory()

    const warnId = () => Number(sessionStorage.getItem('warningTimeout'))
    const idleId = () => Number(sessionStorage.getItem('idleTimeout'))

    const warnIdle = () => {
      if (process.env.NODE_ENV !== 'production') {
        console.info(`You will be logged out in ${(idleTimeout - warningTimeout)/1000} seconds`)
      }
      clearTimeout(warnId())
    }

    const redirectToLogin = () => {
      if (process.env.NODE_ENV !== 'production') {
        console.info('You will be logged out now')
      }
      clearTimeout(idleId())
      clearTimeout(warnId())
      clearStorage()
      history.push('/login')
    }

    const startTimers = () => {
      const newWarning = setTimeout(warnIdle, warningTimeout)
      const newIdle = setTimeout(redirectToLogin, idleTimeout)
      sessionStorage.setItem('warningTimeout', String(newWarning))
      sessionStorage.setItem('idleTimeout', String(newIdle))
    }

    const resetTimers = () => {
      clearTimeout(warnId())
      clearTimeout(idleId())
      startTimers()
    }

    const checkUserAuthentication = () => {
      const user = getUser()
      const jwtRegex = /(^[A-Za-z0-9-_]*\.[A-Za-z0-9-_]*\.[A-Za-z0-9-_]*$)/
      const authToken = user && user.authToken

      const isAuth = authToken && jwtRegex.test(authToken)

      if (!isAuth) {
        history.push('/login')
        return
      }

      client.setAuthToken(authToken)
    }

    useEffect(() => {
      startTimers()
      document.body.addEventListener('mousemove', resetTimers)
      document.body.addEventListener('keydown', resetTimers)
      checkUserAuthentication()

      return () => {
        document.body.removeEventListener('mousemove', resetTimers)
        document.body.removeEventListener('keydown', resetTimers)
        clearTimeout(warnId())
        clearTimeout(idleId())
      }
    }, [])

    useEffect(() => {
      checkUserAuthentication()
    }, [checkUserAuthentication])

    return (
      <Route
        {...routeProps}
        component={component && component}
        render={render && render}
      />
    )
  }
